import React from 'react'
import PropTypes from 'prop-types'
import { defaultTo, find, flatten, head, map, pipe, pluck, prop, propEq, reduce, sum, values } from 'ramda'
import styled, { withTheme } from 'styled-components'
import { Area, AreaChart, Tooltip, XAxis, YAxis } from 'recharts'
import { chartTooltipProps, getHoursData } from '../helpers'
import Card from './Card'
import CardTitle from './CardTitle'
import CardValue from './CardValue'
import Comparsion from './Comparsion'

const Differences = styled('div')`
  margin-top: 10px;
`

const DifferenceValue = styled('div')`
  font-weight: 500;
  & > span {
    color: ${props => props.color};
  }
`

const ChartWrap = styled('div')`
  margin-top: 30px;
`

const getTotalDates = data => {
  return pipe(
    map(prop('dates')),
    flatten,
    reduce((a, { hour, count }) => {
      a[hour] = a[hour] || { hour, count: 0 }
      a[hour].count += count
      return a
    }, {}),
    values
  )(data)
}

const TotalChart = props => {
  const { theme, filter, data, dataCompare } = props

  // const isDateFilterApplied = Boolean(filterDate)

  const storeId = pipe(head, prop('storeId'))(data)
  const totalInCount = pipe(pluck('cumulativeInCounts'), sum)(data)
  const totalCompareInCount = pipe(pluck('cumulativeInCounts'), sum)(dataCompare)

  const countDifference = totalCompareInCount - totalInCount
  const percentDifference = totalInCount === 0
    ? 100
    : ((countDifference / totalInCount) * 100).toFixed(2)
  const differenceColor = countDifference > 0
    ? theme.palette.greenColor
    : countDifference < 0
      ? theme.palette.redColor
      : 'inherit'

  const totalDates = getTotalDates(data)
  const compareTotalDates = getTotalDates(dataCompare)

  const formedHoursArray = getHoursData(totalDates)
  const formedData = map(item => {
    const comparesHourCount = pipe(
      find(propEq('hour', item.hour)),
      prop('count'),
      defaultTo(0)
    )(compareTotalDates)
    return {
      ...item,
      compareCount: comparesHourCount
    }
  }, formedHoursArray)

  return (
    <Card>
      <CardTitle>{storeId}</CardTitle>
      <CardTitle>Total: {totalInCount}</CardTitle>
      <Differences>
        <CardValue>
          <DifferenceValue color={differenceColor}>
            Difference: <span>{countDifference}</span>
          </DifferenceValue>
        </CardValue>
        <CardValue>
          <DifferenceValue color={differenceColor}>
            Diff. percent: <span>{percentDifference}%</span>
          </DifferenceValue>
        </CardValue>
      </Differences>

      <Comparsion
        filter={filter}
        totalInCount={totalInCount}
        totalCompareInCount={totalCompareInCount}
      />

      <ChartWrap>
        <AreaChart
          width={1000}
          height={300}
          data={formedData}
          margin={{
            top: 5,
            right: 30,
            left: -30,
            bottom: 5
          }}
        >
          <XAxis dataKey="hour" />
          <YAxis />
          <Tooltip {...chartTooltipProps} />

          <Area
            type="monotone"
            dataKey="count"
            stroke={theme.palette.greenColor}
            fill={theme.palette.greenColor}
          />
          <Area
            type="monotone"
            dataKey="compareCount"
            stroke={theme.palette.primaryColor}
            fill={theme.palette.primaryColor}
          />
        </AreaChart>
      </ChartWrap>
    </Card>
  )
}

TotalChart.propTypes = {
  theme: PropTypes.object,
  filter: PropTypes.object,
  data: PropTypes.array,
  dataCompare: PropTypes.array,
  filterDate: PropTypes.string
}

export default withTheme(TotalChart)
